// src/rms/i18n/countries.js
// ============================================================================
// Countries, and what they imply
// ============================================================================
// Picking a country answers two other questions for free -- which currency the
// business trades in and which language it probably runs in. Both are stable
// facts, so we fill them in and let the operator correct them.
//
// It does NOT answer a third: what their tax rate is. That is deliberate and it
// is the whole reason this file has no tax column. Rates change, they vary by
// municipality, and a stale rate that an operator trusted becomes their legal
// exposure rather than our display bug. So we ask.
//
// ----------------------------------------------------------------------------
// Country NAMES are not stored here
// ----------------------------------------------------------------------------
// Intl.DisplayNames already knows every country in every language the browser
// supports, so "DE" renders as "Germany", "Allemagne", "ألمانيا" or "ドイツ"
// without a single catalogue entry. Storing English names here would mean
// translating 200 of them by hand into thirteen languages, and getting a
// politically sensitive set of names wrong in public.
//
// `currency` is ISO 4217. `locale` is the language this country most commonly
// does business in, narrowed to the thirteen ReserveZoo ships; where the
// dominant business language is not one of them, it is left null and the
// operator keeps whatever their browser asked for.
// ============================================================================

import { DEFAULT_LOCALE, resolveLocale } from "./locale";

/** [ISO 3166-1 alpha-2, ISO 4217 currency, business language or null] */
const DATA = [
  ["AE", "AED", "ar"], ["AR", "ARS", "es"], ["AT", "EUR", "de"], ["AU", "AUD", "en"],
  ["BD", "BDT", "bn"], ["BE", "EUR", "fr"], ["BG", "BGN", null], ["BH", "BHD", "ar"],
  ["BR", "BRL", "pt"], ["CA", "CAD", "en"], ["CH", "CHF", "de"], ["CL", "CLP", "es"],
  ["CN", "CNY", null], ["CO", "COP", "es"], ["CR", "CRC", "es"], ["CZ", "CZK", null],
  ["DE", "EUR", "de"], ["DK", "DKK", null], ["DO", "DOP", "es"], ["DZ", "DZD", "ar"],
  ["EC", "USD", "es"], ["EE", "EUR", null], ["EG", "EGP", "ar"], ["ES", "EUR", "es"],
  ["FI", "EUR", null], ["FR", "EUR", "fr"], ["GB", "GBP", "en"], ["GH", "GHS", "en"],
  ["GR", "EUR", null], ["GT", "GTQ", "es"], ["HK", "HKD", null], ["HR", "EUR", null],
  ["HU", "HUF", null], ["ID", "IDR", "id"], ["IE", "EUR", "en"], ["IL", "ILS", null],
  ["IN", "INR", "hi"], ["IQ", "IQD", "ar"], ["IT", "EUR", null], ["JO", "JOD", "ar"],
  ["JP", "JPY", "ja"], ["KE", "KES", "en"], ["KR", "KRW", null], ["KW", "KWD", "ar"],
  ["KZ", "KZT", "ru"], ["LB", "LBP", "ar"], ["LK", "LKR", null], ["LT", "EUR", null],
  ["LU", "EUR", "fr"], ["LV", "EUR", null], ["MA", "MAD", "ar"], ["MX", "MXN", "es"],
  ["MY", "MYR", null], ["NG", "NGN", "en"], ["NL", "EUR", null], ["NO", "NOK", null],
  ["NZ", "NZD", "en"], ["OM", "OMR", "ar"], ["PA", "PAB", "es"], ["PE", "PEN", "es"],
  ["PH", "PHP", "en"], ["PK", "PKR", "ur"], ["PL", "PLN", null], ["PT", "EUR", "pt"],
  ["PY", "PYG", "es"], ["QA", "QAR", "ar"], ["RO", "RON", null], ["RS", "RSD", null],
  ["RU", "RUB", "ru"], ["SA", "SAR", "ar"], ["SE", "SEK", null], ["SG", "SGD", "en"],
  ["SI", "EUR", null], ["SK", "EUR", null], ["TH", "THB", null], ["TN", "TND", "ar"],
  ["TR", "TRY", null], ["TW", "TWD", null], ["TZ", "TZS", "en"], ["UA", "UAH", null],
  ["UG", "UGX", "en"], ["US", "USD", "en"], ["UY", "UYU", "es"], ["VN", "VND", null],
  ["ZA", "ZAR", "en"],
];

/**
 * What the operator's tax registration number is CALLED where they trade.
 *
 * This is the one tax-adjacent thing this file will state, and the distinction
 * from the rate above it is the whole point: a rate we guess wrong becomes the
 * operator's liability, while a LABEL we guess wrong is a word on an invoice
 * that they can correct in one field. So the rate is asked for and the wording
 * is offered.
 *
 * It matters because the tax and its number are frequently not called the same
 * thing -- Australia charges GST and issues an ABN, India charges GST and
 * issues a GSTIN -- so the term cannot be derived from the rate names in the
 * operator's library, which is the obvious-looking shortcut.
 *
 * Written the way it appears BESIDE the number on an invoice ("VAT No.
 * GB123456789"), because that is where it is read most often. Settings uses the
 * same string as the field label.
 *
 * Absent means we have no term we would stand behind, not that the country has
 * no tax. The United States is deliberately absent: sales tax is registered
 * state by state and a number is not conventionally printed on an invoice at
 * all. Callers fall back to a generic.
 */
const TAX_LABELS = {
  AE: "TRN",          AT: "UID-Nr.",      AU: "ABN",          BD: "BIN",
  BE: "BTW-nr.",      BR: "CNPJ",         CA: "GST/HST No.",  CH: "MWST-Nr.",
  CZ: "DIČ",          DE: "USt-IdNr.",    DK: "CVR-nr.",      ES: "NIF",
  FI: "ALV-numero",   FR: "No. TVA",      GB: "VAT No.",      GR: "ΑΦΜ",
  HU: "Adószám",      ID: "NPWP",         IE: "VAT No.",      IN: "GSTIN",
  IT: "P. IVA",       JP: "登録番号",       KE: "PIN",          MX: "RFC",
  NG: "TIN",          NL: "Btw-nummer",   NO: "MVA-nr.",      NZ: "GST No.",
  PH: "TIN",          PK: "NTN",          PL: "NIP",          PT: "NIF",
  RO: "CUI",          RU: "ИНН",          SA: "VAT No.",      SE: "Momsreg.nr",
  SG: "GST Reg. No.", TR: "Vergi No.",    ZA: "VAT No.",
};

/**
 * The term this country uses, or null when we have none. Null is a real answer:
 * the caller shows a generic rather than inventing one.
 */
export const taxLabelForCountry = (code) => TAX_LABELS[code] || null;

export const COUNTRIES = DATA.map(([code, currency, locale]) => ({ code, currency, locale }));

const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));

export const isSupportedCountry = (code) => BY_CODE.has(code);

/** The currency a business in this country most likely trades in. */
export const currencyForCountry = (code) => BY_CODE.get(code)?.currency || "USD";

/**
 * The language a business in this country most likely runs in, narrowed to the
 * locales actually shipped. Null when none of them fit, so the caller can keep
 * whatever the browser asked for instead of forcing English on, say, a Thai
 * operator whose browser is already set to something sensible.
 */
export const localeForCountry = (code) => resolveLocale(BY_CODE.get(code)?.locale) || null;

/** Every distinct currency any listed country uses, for the currency select. */
export const CURRENCIES = [...new Set(COUNTRIES.map((c) => c.currency))].sort();

/**
 * Countries as { value, label } for a <Select>, named in `locale` and sorted
 * the way that language sorts them -- "Ägypten" files under A in German, and
 * Intl.Collator is the only thing that gets that right.
 */
export function countryOptions(locale = DEFAULT_LOCALE) {
  let name = (code) => code;
  try {
    const display = new Intl.DisplayNames([locale], { type: "region" });
    name = (code) => display.of(code) || code;
  } catch {
    // Very old browser, or a locale Intl has no region data for. Codes are a
    // poor label but an honest one, and the form still works.
  }

  let collator = null;
  try {
    collator = new Intl.Collator(locale);
  } catch {
    collator = null;
  }

  return COUNTRIES.map((c) => ({ value: c.code, label: name(c.code) })).sort((a, b) =>
    collator ? collator.compare(a.label, b.label) : a.label.localeCompare(b.label)
  );
}

export default { COUNTRIES, CURRENCIES, countryOptions, currencyForCountry, localeForCountry, taxLabelForCountry };
